import { useFusionAuth } from "@fusionauth/react-sdk";
import { ChevronRight } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useCallback, useContext, useEffect, useState } from "react";
import CloseArrowTooltip from "../../../components/CloseArrowTooltip";
import {
  ProjectContext,
  ProjectDispatchContext,
} from "../../../contexts/ProjectContext";
import {
  convertProjectContextToProject,
  convertProjectToProjectContext,
} from "../../../models/Project";
import { useFetch } from "../../../services/hooks/useFetch";
import theme from "../../../styles/theme";
import { apiPrefix } from "../../../utils/main";

dayjs.extend(timezone);
dayjs.extend(utc);

export default function ProtocolPage() {
  const [editMode, setEditMode] = useState(false);

  const project = useContext(ProjectContext)!;
  const dispatch = useContext(ProjectDispatchContext);

  const {
    update: updateProjectProtocol,
    error: updateProjectProtocolError,
    status: updateProjectProtocolStatus,
  } = useFetch<undefined, ProjectRequest, Project>();

  const [startDate, setStartDate] = useState(dayjs(Date.now()));
  const [endDate, setEndDate] = useState(dayjs(Date.now()));
  const [daysPreIndex, setDaysPreIndex] = useState<number | undefined>(0);
  const [daysPostIndex, setDaysPostIndex] = useState<number | undefined>(0);
  const [details, setDetails] = useState("");
  const [updateDetails, setUpdateDetails] = useState("");

  const [notesPreIndex, setNotesPreIndex] = useState("");
  const [notesPostIndex, setNotesPostIndex] = useState("");
  const [medicationsPreIndex, setMedicationsPreIndex] = useState("");
  const [medicationsPostIndex, setMedicationsPostIndex] = useState("");
  const [displayErrors, setDisplayErrors] = useState(false);

  const { userInfo } = useFusionAuth();
  const isAdmin = userInfo?.roles?.includes("ADMIN");

  // Populate this page with data from the project
  const setValuesFromProject = useCallback(
    (project: ProjectContextType) => {
      if (!project) return;

      if (project.startDate) {
        setStartDate(dayjs.tz(project.startDate, "UTC"));
      }

      if (project.endDate) {
        setEndDate(dayjs.tz(project.endDate, "UTC"));
      }

      if (project.defaultPreIndexDays) {
        setDaysPreIndex(project.defaultPreIndexDays);
      }

      if (project.defaultPostIndexDays) {
        setDaysPostIndex(project.defaultPostIndexDays);
      }

      if (project.additionalCriteria) {
        setDetails(project.additionalCriteria);
      }

      const projectNotesPreIndex = project.projectSettings?.find(
        (s) => s.name === "NOTES_PRE_INDEX",
      )?.value;
      if (typeof projectNotesPreIndex !== "undefined") {
        setNotesPreIndex(projectNotesPreIndex);
      } else {
        setNotesPreIndex("");
      }

      const projectNotesPostIndex = project.projectSettings?.find(
        (s) => s.name === "NOTES_POST_INDEX",
      )?.value;
      if (typeof projectNotesPostIndex !== "undefined") {
        setNotesPostIndex(projectNotesPostIndex);
      } else {
        setNotesPostIndex("");
      }

      if (isAdmin) {
        const projectMedicationsPreIndex = project.projectSettings?.find(
          (s) => s.name === "MEDICATIONS_PRE_INDEX",
        )?.value;
        if (typeof projectMedicationsPreIndex !== "undefined") {
          setMedicationsPreIndex(projectMedicationsPreIndex);
        } else {
          setMedicationsPreIndex("");
        }

        const projectMedicationsPostIndex = project.projectSettings?.find(
          (s) => s.name === "MEDICATIONS_POST_INDEX",
        )?.value;
        if (typeof projectMedicationsPostIndex !== "undefined") {
          setMedicationsPostIndex(projectMedicationsPostIndex);
        } else {
          setMedicationsPostIndex("");
        }
      }
    },
    [isAdmin],
  );

  useEffect(() => {
    let initialLoad = true;
    if (initialLoad) {
      setValuesFromProject(project!);
    }
    return () => {
      initialLoad = false;
    };
  }, [project, setValuesFromProject]);

  const updateProjectSetting = function (
    project: ProjectContextType,
    settingName: string,
    settingValue: string,
  ) {
    const projectSetting = project.projectSettings?.find(
      (s) => s.name === settingName,
    );
    const hasValue =
      typeof settingValue !== "undefined" &&
      settingValue !== null &&
      settingValue !== "";

    if (projectSetting && !hasValue) {
      // Delete setting
      project.projectSettings = project.projectSettings?.filter(
        (s) => s.name !== settingName,
      );
    } else if (projectSetting && hasValue) {
      // Update setting
      projectSetting["value"] = settingValue;
    } else if (!projectSetting && hasValue) {
      // Create setting
      project.projectSettings?.push({
        name: settingName,
        value: settingValue,
      });
    }
  };

  // Update project object with data from this page and submit it.
  const submitProjectSettings = function () {
    if (
      !project ||
      startDate === null ||
      endDate === null ||
      daysPreIndex === undefined ||
      daysPreIndex < 0 ||
      daysPostIndex === undefined ||
      daysPostIndex < 0 ||
      parseInt(notesPreIndex) < 0 ||
      parseInt(notesPostIndex) < 0 ||
      parseInt(medicationsPreIndex) < 0 ||
      parseInt(medicationsPostIndex) < 0 ||
      updateDetails === ""
    ) {
      setDisplayErrors(true);
      return;
    }

    project.startDate = startDate.toDate();
    project.endDate = endDate.toDate();
    project.defaultPreIndexDays = daysPreIndex;
    project.defaultPostIndexDays = daysPostIndex;
    project.additionalCriteria = details;

    updateProjectSetting(project, "NOTES_PRE_INDEX", notesPreIndex);
    updateProjectSetting(project, "NOTES_POST_INDEX", notesPostIndex);
    updateProjectSetting(project, "MEDICATIONS_PRE_INDEX", medicationsPreIndex);
    updateProjectSetting(
      project,
      "MEDICATIONS_POST_INDEX",
      medicationsPostIndex,
    );

    void updateProjectProtocol({
      path: `${apiPrefix}/projects/${project.id}`,
      body: {
        reason: updateDetails,
        project: convertProjectContextToProject(project),
      },
      responseManipulationFunction: (data) => {
        convertProjectToProjectContext(data, dispatch);
        return;
      },
    });
  };

  useEffect(() => {
    if (updateProjectProtocolStatus) {
      if (updateProjectProtocolStatus === 200) {
        setUpdateDetails("");
        setEditMode(false);
        setDisplayErrors(false);
      } else {
        setDisplayErrors(true);
      }
    }
  }, [updateProjectProtocolStatus]);

  return (
    <Grid container direction={"column"} className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Protocol</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">
            Inclusion/exclusion criteria parameters used to curate the patient
            cohort and the longitudinal data included in the project.
          </Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">* Required fields</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body1">Procedure Date Range</Typography>
        </Grid>
        <br />
        <Grid container justifyContent={"space-between"}>
          <Grid>
            {editMode && isAdmin ? (
              <FormControl error={displayErrors && startDate === null}>
                <DatePicker
                  readOnly={!editMode || !isAdmin}
                  label={"Start Date *"}
                  sx={{ width: "30ch" }}
                  value={startDate}
                  onChange={(event) => {
                    setStartDate(dayjs.tz(event, "UTC"));
                  }}
                />
                <FormHelperText>
                  {displayErrors && startDate === null
                    ? "This field is required"
                    : ""}
                </FormHelperText>
              </FormControl>
            ) : (
              <>
                <Typography variant="caption">Start Date *</Typography>
                <Typography variant="body1" sx={{ width: "30ch" }}>
                  {startDate.format("MM/DD/YYYY")}
                </Typography>
              </>
            )}
          </Grid>
          <Grid>
            <ChevronRight />
          </Grid>
          <Grid>
            {editMode && isAdmin ? (
              <FormControl error={displayErrors && endDate === null}>
                <DatePicker
                  readOnly={!editMode || !isAdmin}
                  label={"End Date *"}
                  sx={{ width: "30ch" }}
                  value={endDate}
                  onChange={(event) => {
                    setEndDate(dayjs.tz(event, "UTC"));
                  }}
                />
                <FormHelperText>
                  {displayErrors && endDate === null
                    ? "This field is required"
                    : ""}
                </FormHelperText>
              </FormControl>
            ) : (
              <>
                <Typography variant="caption">End Date *</Typography>
                <Typography variant="body1" sx={{ width: "30ch" }}>
                  {endDate.format("MM/DD/YYYY")}
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body1">Default Date Range</Typography>
        </Grid>
        <br />
        <Grid container justifyContent={"space-between"}>
          <Grid>
            {editMode && isAdmin ? (
              <TextField
                required
                label={"Days Pre-Index Procedure"}
                type="number"
                sx={{ width: "30ch" }}
                slotProps={{
                  htmlInput: {
                    readOnly: !editMode || !isAdmin,
                  },
                  inputLabel: {
                    shrink: true,
                  },
                }}
                value={daysPreIndex}
                error={daysPreIndex === undefined || daysPreIndex < 0}
                helperText={
                  (daysPreIndex === undefined
                    ? "This field is required. "
                    : "") + "Please enter a value greater than or equal to 0."
                }
                onChange={(event) => {
                  const val = event.target.value;
                  setDaysPreIndex(val === "" ? undefined : parseInt(val));
                }}
              ></TextField>
            ) : (
              <>
                <Typography variant="caption">
                  Days Pre-Index Procedure *
                </Typography>
                <Typography variant="body1" sx={{ width: "30ch" }}>
                  {daysPreIndex}
                </Typography>
              </>
            )}
          </Grid>
          <Grid>
            <ChevronRight />
          </Grid>
          <Grid>
            {editMode && isAdmin ? (
              <TextField
                required
                label={"Days Post-Index Procedure"}
                type="number"
                sx={{ width: "30ch" }}
                slotProps={{
                  htmlInput: {
                    readOnly: !editMode || !isAdmin,
                  },
                  inputLabel: {
                    shrink: true,
                  },
                }}
                value={daysPostIndex}
                error={daysPostIndex === undefined || daysPostIndex < 0}
                helperText={
                  (daysPostIndex === undefined
                    ? "This field is required. "
                    : "") + "Please enter a value greater than or equal to 0."
                }
                onChange={(event) => {
                  const val = event.target.value;
                  setDaysPostIndex(val === "" ? undefined : parseInt(val));
                }}
              />
            ) : (
              <>
                <Typography variant="caption">
                  Days Post-Index Procedure *
                </Typography>
                <Typography variant="body1" sx={{ width: "30ch" }}>
                  {daysPostIndex}
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
        {isAdmin && (
          <Grid>
            <br />
            <Grid>
              <Typography variant="body1">
                Notes Date Range{" "}
                <Tooltip
                  title="Empty value for this setting will use the default date range value."
                  placement="top-end"
                >
                  <InfoIcon
                    sx={{
                      color: theme.palette.info.main,
                      verticalAlign: "middle",
                    }}
                  />
                </Tooltip>
              </Typography>
            </Grid>
            <br />
            <Grid container justifyContent={"space-between"}>
              <Grid>
                {editMode && isAdmin ? (
                  <TextField
                    label={"Days Pre-Index Procedure"}
                    type="number"
                    sx={{ width: "30ch" }}
                    slotProps={{
                      htmlInput: {
                        readOnly: !editMode || !isAdmin,
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    value={notesPreIndex}
                    onChange={(event) => {
                      setNotesPreIndex(event.target.value);
                    }}
                    error={parseInt(notesPreIndex) < 0}
                    helperText={
                      parseInt(notesPreIndex) < 0
                        ? "Please enter a value greater than or equal to 0."
                        : ""
                    }
                  ></TextField>
                ) : (
                  <>
                    <Typography variant="caption">
                      Days Pre-Index Procedure
                    </Typography>
                    <Typography variant="body1" sx={{ width: "30ch" }}>
                      {notesPreIndex}
                    </Typography>
                  </>
                )}
              </Grid>
              <Grid>
                <ChevronRight />
              </Grid>
              <Grid>
                {editMode && isAdmin ? (
                  <TextField
                    label={"Days Post-Index Procedure"}
                    type="number"
                    sx={{ width: "30ch" }}
                    slotProps={{
                      htmlInput: {
                        readOnly: !editMode || !isAdmin,
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    value={notesPostIndex}
                    onChange={(event) => {
                      setNotesPostIndex(event.target.value);
                    }}
                    error={parseInt(notesPostIndex) < 0}
                    helperText={
                      parseInt(notesPostIndex) < 0
                        ? "Please enter a value greater than or equal to 0."
                        : ""
                    }
                  />
                ) : (
                  <>
                    <Typography variant="caption">
                      Days Post-Index Procedure
                    </Typography>
                    <Typography variant="body1" sx={{ width: "30ch" }}>
                      {notesPostIndex}
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
        <br />

        {isAdmin && project?.medicationsEnabled && (
          <Grid>
            <Grid>
              <Typography variant="body1">
                Medications Date Range{" "}
                <Tooltip
                  title="Empty value for this setting will use the default date range value."
                  placement="top-end"
                >
                  <InfoIcon
                    sx={{
                      color: theme.palette.info.main,
                      verticalAlign: "middle",
                    }}
                  />
                </Tooltip>
              </Typography>
            </Grid>
            <br />
            <Grid container justifyContent={"space-between"}>
              <Grid>
                {editMode && isAdmin ? (
                  <TextField
                    label={"Days Pre-Index Procedure"}
                    type="number"
                    sx={{ width: "30ch" }}
                    slotProps={{
                      htmlInput: {
                        readOnly: !editMode || !isAdmin,
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    value={medicationsPreIndex}
                    onChange={(event) => {
                      setMedicationsPreIndex(event.target.value);
                    }}
                    error={parseInt(medicationsPreIndex) < 0}
                    helperText={
                      parseInt(medicationsPreIndex) < 0
                        ? "Please enter a value greater than or equal to 0."
                        : ""
                    }
                  ></TextField>
                ) : (
                  <>
                    <Typography variant="caption">
                      Days Pre-Index Procedure
                    </Typography>
                    <Typography variant="body1" sx={{ width: "30ch" }}>
                      {medicationsPreIndex}
                    </Typography>
                  </>
                )}
              </Grid>
              <Grid>
                <ChevronRight />
              </Grid>
              <Grid>
                {editMode && isAdmin ? (
                  <TextField
                    label={"Days Post-Index Procedure"}
                    type="number"
                    sx={{ width: "30ch" }}
                    slotProps={{
                      htmlInput: {
                        readOnly: !editMode || !isAdmin,
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    value={medicationsPostIndex}
                    onChange={(event) => {
                      setMedicationsPostIndex(event.target.value);
                    }}
                    error={parseInt(medicationsPostIndex) < 0}
                    helperText={
                      parseInt(medicationsPostIndex) < 0
                        ? "Please enter a value greater than or equal to 0."
                        : ""
                    }
                  />
                ) : (
                  <>
                    <Typography variant="caption">
                      Days Post-Index Procedure
                    </Typography>
                    <Typography variant="body1" sx={{ width: "30ch" }}>
                      {medicationsPostIndex}
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
        <br />
        <Grid>
          <Typography variant="body1">
            List any specific inclusion or exclusion criteria
          </Typography>
        </Grid>
        <br />
        <Grid>
          {editMode ? (
            <TextField
              label="Details"
              multiline
              minRows={4}
              sx={{ width: "50ch" }}
              slotProps={{
                htmlInput: {
                  readOnly: !editMode,
                },
              }}
              value={details}
              onChange={(event) => {
                setDetails(event.target.value);
              }}
            ></TextField>
          ) : (
            <>
              <Typography variant="caption">Details</Typography>
              <Typography variant="body1" sx={{ width: "50ch" }}>
                {details}
              </Typography>
            </>
          )}
        </Grid>
        <br />
        {editMode && (
          <Grid>
            <TextField
              required
              label="Note/Reason for Updating Protocol"
              multiline
              minRows={2}
              sx={{ width: "50ch" }}
              slotProps={{
                htmlInput: {
                  readOnly: !editMode,
                },
              }}
              value={updateDetails}
              error={displayErrors && updateDetails === ""}
              helperText={
                displayErrors && updateDetails === ""
                  ? "This field is required"
                  : ""
              }
              onChange={(event) => {
                setUpdateDetails(event.target.value);
              }}
            ></TextField>
          </Grid>
        )}
        <br />
        {displayErrors && !!updateProjectProtocolError && (
          <>
            <Alert severity="error">
              {typeof updateProjectProtocolError === "object" &&
              "message" in updateProjectProtocolError
                ? String(updateProjectProtocolError.message)
                : String(updateProjectProtocolError)}
            </Alert>
            <br />
          </>
        )}
        <Grid container justifyContent={"right"}>
          {(editMode && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setUpdateDetails("");
                  setEditMode(false);
                  setDisplayErrors(false);
                  setValuesFromProject(project);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="update-button"
                sx={{ marginLeft: "5px" }}
                onClick={() => {
                  submitProjectSettings();
                }}
              >
                Update
              </Button>
            </>
          )) || (
            <CloseArrowTooltip
              title={project?.isLocked ? "This project is locked." : ""}
            >
              <span>
                <Button
                  variant="contained"
                  onClick={() => setEditMode(true)}
                  disabled={project?.isLocked}
                >
                  Edit
                </Button>
              </span>
            </CloseArrowTooltip>
          )}
        </Grid>
      </div>
    </Grid>
  );
}
